import { useCallback, useEffect, useState } from "react";

import {
	type ChoiceTunable,
	type NumberTunable,
	parseTunable,
	type ToggleTunable,
	type Tunable,
} from "../../shared/tunables";

type Raw = Record<string, string>;

let cache: Raw | null = null;
let inflight: Promise<Raw> | null = null;
const listeners = new Set<(raw: Raw) => void>();

function load(): Promise<Raw> {
	if (!inflight) {
		inflight = fetch("/api/settings")
			.then((r) => r.json())
			.then((d: { settings: Raw }) => {
				cache = d.settings;
				return cache;
			})
			.finally(() => {
				inflight = null;
			});
	}
	return inflight;
}

export type TunableGetter = {
	(t: NumberTunable): number;
	(t: ToggleTunable): boolean;
	<T extends string>(t: ChoiceTunable<T>): T;
};

/**
 * The saved settings, shared by every page that reads a tunable: one fetch
 * per page load, and a save on the Settings page updates the others at once.
 * Until the fetch lands, every tunable reads as its default.
 */
export function useTunables(): {
	get: TunableGetter;
	save: (t: Tunable, value: string | number | boolean) => Promise<string | null>;
	ready: boolean;
} {
	const [raw, setRaw] = useState<Raw | null>(cache);

	useEffect(() => {
		listeners.add(setRaw);
		if (!cache) void load().then(setRaw);
		return () => {
			listeners.delete(setRaw);
		};
	}, []);

	const get = useCallback(((t: Tunable) => parseTunable(t, raw?.[t.key])) as TunableGetter, [raw]);

	/** Saves one value; resolves to the server's error, or null on success. */
	const save = useCallback(async (t: Tunable, value: string | number | boolean) => {
		const res = await fetch("/api/settings", {
			method: "PATCH",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ [t.key]: value }),
		});
		if (!res.ok) {
			const body = (await res.json().catch(() => ({}))) as { error?: string };
			return body.error ?? `HTTP ${res.status}`;
		}
		cache = { ...(cache ?? {}), [t.key]: String(value) };
		for (const l of listeners) l(cache);
		return null;
	}, []);

	return { get, save, ready: raw != null };
}
